import { useEffect, useState } from "react";
import Browser from "bowser";

// WebKit, Blink, Gecko, Trident, Presto, EdgeHTML
/**
 * WebKit： Safari 浏览器
 * Blink： Chrome 系列（包括 Chrome、Chromium、新版 Edge 等）
 * Gecko： Firefox 浏览器
 * Trident（已不再使用）： Internet Explorer、360极速浏览器
 * Presto（已不再使用）： Opera 浏览器（Presto引擎在Opera 15及以后版本被弃用）
 * EdgeHTML： 旧版 Edge 浏览器
 */
const SUPPORT_BROWSER_ENGINE = [
  // Browser.ENGINE_MAP.EdgeHTML,
  Browser.ENGINE_MAP.Blink,
  // Browser.ENGINE_MAP.Trident,
  // Browser.ENGINE_MAP.Presto,
  Browser.ENGINE_MAP.Gecko,
  Browser.ENGINE_MAP.WebKit,
];
// https://github.com/bowser-js/bowser/blob/master/src/constants.js
const SUPPORT_BROWSER_VERSION = {
  // https://chromereleases.googleblog.com/
  // Release January 19, 2021, 88.0.4324.96 (for Mac and Linux) and 88.0.4324.104 for windows
  // updated to 94.0.4606.61 for Windows, Mac and Linux
  // 2025.01 升级 88 → 94（要求 >= 95）
  chrome: '>=95',
  // Cursor 内置浏览器、Electron、部分环境会被 bowser 识别为 Chromium
  // 这里按 Chrome 同档位策略处理
  chromium: '>=95',
  // Electron 应用（如 Cursor 内置浏览器）使用 Chromium 内核，按 Chrome 同档位策略处理
  electron: '>=95',

  // https://www.mozilla.org/en-US/firefox/releases/
  // Release April 19, 2021, Version 88.0
  // Version 93.0,Release channel users on October 5, 2021
  // 2025.01 升级 88 → 93（要求 >= 94）
  firefox: '>=94',

  // https://developer.apple.com/documentation/safari-release-notes/
  // Released September 16, 2020 — Version 14 (16610.1.28)
  // Released September 20, 2021 — Version 15 (17612.1.27)
  // 2025.01 升级 14 → 15（要求 >= 16）
  safari: '>=16',

  // https://learn.microsoft.com/en-us/deployedge/microsoft-edge-release-schedule#microsoft-edge-releases
  // 21-Jan-2021, Version 88.0.705.50
  // Released Version	02-Sep-2021 94.0.992.9
  // 2025.01 升级 88 → 94（要求 >= 95）
  edge: '>=95',
};

export type BrowserCompatibilityStatus = 'supported' | 'unsupported' | 'unknown';

/**
 * 检测是否是360浏览器（非Chrome内核）
 * 360安全浏览器（360SE）和360极速浏览器旧版使用Trident内核，需要提示升级
 * 360极速浏览器新版使用Chrome内核（Blink），应该被支持
 */
const is360BrowserNonChrome = (browser: Browser.Parser.Parser, userAgent: string) => {
  // 检测User-Agent中是否包含360浏览器标识
  const is360Browser = /360SE|360EE|360Browser|QihooBrowser/i.test(userAgent);
  if (!is360Browser) {
    return false;
  }
  // 如果是360浏览器，但引擎是Blink（Chrome内核），则支持
  // 只有非Blink内核的360浏览器才需要提示升级
  const engineName = browser.getEngineName();
  return engineName !== Browser.ENGINE_MAP.Blink;
};

const getBrowserEngineCompatibility = (browser: Browser.Parser.Parser): BrowserCompatibilityStatus => {
  // iOS/iPadOS 上的浏览器内核统一为 WebKit，即使被识别为其他引擎（如 EdgeHTML）也视为支持
  // 版本控制完全交给 getBrowserVersionCompatibility 处理
  const osName = browser.getOSName();
  if (osName === 'iOS' || osName === 'iPadOS') {
    return 'supported';
  }
  const engineName = browser.getEngineName();
  if (SUPPORT_BROWSER_ENGINE.includes(engineName)) {
    return 'supported';
  }
  if ([
    Browser.ENGINE_MAP.EdgeHTML,
    Browser.ENGINE_MAP.Trident,
    Browser.ENGINE_MAP.Presto,
  ].includes(engineName)) {
    return 'unsupported';
  }
  return 'unknown';
};

const parseMajorVersionFromUserAgent = (userAgent: string, engineName: string) => {
  const ua = userAgent || '';

  // Blink：Chrome / Chromium / Edge / Electron / HeadlessChrome
  // 注意：Electron/Cursor 的 UA 通常包含 "Chrome/xxx"，优先匹配 Chrome 版本号
  if (engineName === Browser.ENGINE_MAP.Blink) {
    // 优先匹配 Chrome 版本（Electron/Cursor 的 UA 通常包含 Chrome/xxx）
    const chromeMatch = ua.match(/Chrome\/(\d+)/i);
    if (chromeMatch?.[1]) {
      return Number.parseInt(chromeMatch[1], 10);
    }
    // 兜底：匹配其他 Blink 引擎标识
    const match = ua.match(/(?:HeadlessChrome|Chromium|Edg)\/(\d+)/i);
    if (match?.[1]) {
      return Number.parseInt(match[1], 10);
    }
  }

  // Gecko：Firefox
  if (engineName === Browser.ENGINE_MAP.Gecko) {
    const match = ua.match(/Firefox\/(\d+)/i);
    if (match?.[1]) {
      return Number.parseInt(match[1], 10);
    }
  }

  // WebKit：Safari（注意 Chrome UA 也包含 Safari 字样，因此只在 WebKit 引擎时解析）
  if (engineName === Browser.ENGINE_MAP.WebKit) {
    const match = ua.match(/Version\/(\d+)/i);
    if (match?.[1]) {
      return Number.parseInt(match[1], 10);
    }
  }

  return undefined;
};

const getMinSupportedMajorVersionByEngine = (engineName: string) => {
  // 这里保持与 SUPPORT_BROWSER_VERSION 的阈值一致：
  // chrome/chromium/edge: >94（即主版本 >= 95）
  // firefox: >93（即主版本 >= 94）
  // safari: >15（即主版本 >= 16）
  if (engineName === Browser.ENGINE_MAP.Blink) {
    return 95;
  }
  if (engineName === Browser.ENGINE_MAP.Gecko) {
    return 94;
  }
  if (engineName === Browser.ENGINE_MAP.WebKit) {
    return 16;
  }
  return undefined;
};

const getBrowserVersionCompatibility = (
  browser: Browser.Parser.Parser,
  userAgent: string,
): BrowserCompatibilityStatus => {
  const browserName = browser.getBrowserName();
  const engineName = browser.getEngineName();
  const osName = browser.getOSName();

  // 1. iOS/iPadOS 设备版本检查 (拦截 iOS 上的 Chrome/Edge 等)
  // 无论用什么浏览器，iOS 上的内核都是 WebKit，且受限于系统版本
  // 即使 Bowser 认为 Chrome 120 > 94，如果跑在 iOS 14 上，仍然是不支持的
  if (osName === 'iOS' || osName === 'iPadOS') {
    const osVersion = browser.getOSVersion();
    if (osVersion) {
      const majorVersion = parseInt(osVersion.split('.')[0], 10);
      // iOS < 16 (Safari < 16) 不支持
      // 虽然 bowser 配置写的是 >15，但实际意图是主版本 >= 16 (见 getMinSupportedMajorVersionByEngine)
      if (!Number.isNaN(majorVersion) && majorVersion < 16) {
        return 'unsupported';
      }
      // iOS >= 16，直接支持。
      // 注意：这里直接返回 true，跳过后续针对 Chrome/Edge 等壳版本的检查，
      // 因为 iOS 上壳版本很高（如 Chrome 120）没有任何意义，核心是 iOS 系统版本。
      if (!Number.isNaN(majorVersion)) {
        return 'supported';
      }
      return 'unknown';
    }
    return 'unknown';
  }

  // 特殊处理：Electron/Cursor 等应用使用 Chromium 内核
  // 它们的 Electron 版本号与 Chrome 版本号不同，应该检查 UA 中的 Chrome 版本
  // 例如：Electron@37.7.0 但 UA 中包含 Chrome/138，应该按 Chrome 138 来判断
  if (browserName === 'Electron' && engineName === Browser.ENGINE_MAP.Blink) {
    const minMajor = getMinSupportedMajorVersionByEngine(engineName);
    if (!minMajor) {
      return 'unknown';
    }
    const major = parseMajorVersionFromUserAgent(userAgent, engineName);
    if (typeof major === "number" && Number.isFinite(major)) {
      return major >= minMajor ? 'supported' : 'unsupported';
    }
    return 'unknown';
  }

  const result = browser.satisfies(SUPPORT_BROWSER_VERSION);
  // bowser 2.x 在无法识别 UA 时返回 undefined：
  // - 常见于某些 Chromium UA
  // - 这里做一次"按引擎解析主版本号"的兜底，避免误伤
  if (typeof result === "boolean") {
    // 对于 WebKit 引擎（Safari），即使 satisfies 返回 true，也要进行二次验证
    // 因为某些边界情况下 bowser 可能误判（例如 Safari 15.x 会被判定为满足 '>15'）
    // 我们要求的是主版本号 >= 16，所以需要额外检查
    // 虽然配置已升级为 '>=16'，为了双重保险，保留此逻辑
    if (result === true && engineName === Browser.ENGINE_MAP.WebKit) {
      // 修复：如果 Bowser 已经识别出这是 Chrome/Edge 等非 Safari 浏览器，且版本满足要求，
      // 就不应该再用 Version/xxx (Safari版本) 去卡它。
      // 只有当浏览器确实是 Safari 时，才强制检查 Version 字段。
      if (browserName !== 'Safari') {
        return 'supported';
      }

      const minMajor = getMinSupportedMajorVersionByEngine(engineName);
      if (minMajor) {
        const major = parseMajorVersionFromUserAgent(userAgent, engineName);
        if (typeof major === "number" && Number.isFinite(major)) {
          // 如果解析出的主版本号小于要求的最小版本，则拦截
          return major >= minMajor ? 'supported' : 'unsupported';
        }
        return 'unknown';
      }
    }
    return result ? 'supported' : 'unsupported';
  }

  const minMajor = getMinSupportedMajorVersionByEngine(engineName);
  if (!minMajor) {
    return 'unknown';
  }

  const major = parseMajorVersionFromUserAgent(userAgent, engineName);
  if (typeof major === "number" && Number.isFinite(major)) {
    return major >= minMajor ? 'supported' : 'unsupported';
  }

  return 'unknown';
};

const isFindinDesktopWebView = (userAgent: string) => {
  return /FindinDesktop\/Tauri/i.test(userAgent);
};

export const getBrowserCompatibility = (userAgent: string): BrowserCompatibilityStatus => {
  if (isFindinDesktopWebView(userAgent)) {
    return 'supported';
  }

  try {
    const browser = Browser.getParser(userAgent);

    if (is360BrowserNonChrome(browser, userAgent)) {
      return 'unsupported';
    }

    const engineCompatibility = getBrowserEngineCompatibility(browser);
    if (engineCompatibility !== 'supported') {
      return engineCompatibility;
    }
    return getBrowserVersionCompatibility(browser, userAgent);
  } catch {
    return 'unknown';
  }
};

export const useBrowserCompatibility = () => {
  // 默认是按支持处理
  const [status, setStatus] = useState<BrowserCompatibilityStatus>('supported');

  useEffect(() => {
    if (typeof window === 'undefined') {
      setStatus('unknown');
      return;
    }
    const userAgent = window.navigator.userAgent;
    const compatibility = getBrowserCompatibility(userAgent);
    setStatus(compatibility);

    if (compatibility !== 'supported' && process.env.NODE_ENV !== 'production') {
      try {
        const browser = Browser.getParser(userAgent);
        // eslint-disable-next-line no-console
        console.warn(`[browser-support] ${compatibility}`, {
          os: `${browser.getOSName?.() ?? ''}@${browser.getOSVersion?.() ?? ''}`,
          browser: `${browser.getBrowserName?.() ?? ''}@${browser.getBrowserVersion?.() ?? ''}`,
          engine: browser.getEngineName?.(),
          userAgent,
        });
      } catch {
        // eslint-disable-next-line no-console
        console.warn(`[browser-support] ${compatibility}`, { userAgent });
      }
    }
  }, []);

  return status;
};

export const useBrowserSupport = () => {
  const compatibility = useBrowserCompatibility();
  return compatibility !== 'unsupported';
};

export const useBrowser = () => {
  const [browser, setBrowser] = useState<Browser.Parser.Parser>();

  useEffect(() => {
    if (typeof window === 'undefined') {
      setBrowser(undefined);
      return;
    }
    const browser = Browser.getParser(window.navigator.userAgent);
    setBrowser(browser);
  }, []);

  return browser;
};
